import React, { useEffect, useState } from "react";
import { Alert, Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { retryEInvoice, cancelEInvoiceClient, getEInvoiceDetail } from "../generic/crud";
import { showToast, TOAST_SUCCESS, TOAST_FAILURE } from "../../utils/toast/Toast";

// NIC e-Invoice cancellation reasons — NOT the same codes as the e-Way bill's.
const CANCEL_REASONS = [
    { value: 1, label: "Duplicate" },
    { value: 2, label: "Data entry mistake" },
    { value: 3, label: "Order cancelled" },
    { value: 4, label: "Others" },
];

/** Map an NIC/validation error to a concrete "do this next" hint for the operator. */
function nextSteps(error) {
    const msg = String(error || "").toLowerCase();
    const steps = [];
    if (msg.includes("gstin") || msg.includes("gst number")) {
        steps.push({
            icon: "bi-person-vcard",
            text: "An e-Invoice needs the customer's GSTIN. Open Customers and add it — the fix syncs to the server automatically. Customers without a GSTIN can only get an e-Way bill.",
        });
    }
    if (msg.includes("invoice number")) {
        steps.push({
            icon: "bi-hash",
            text: "The portal rejects invoice numbers that start with 0, / or - or are shorter than 3 characters. Ask your admin to correct the invoice numbering on the server.",
        });
    }
    if (msg.includes("hsn")) {
        steps.push({
            icon: "bi-upc-scan",
            text: "Fix the HSN code on the inventory/service item (4, 6 or 8 digits; 6+ when an e-Way bill is generated too).",
        });
    }
    if (msg.includes("pin") || msg.includes("pincode")) {
        steps.push({
            icon: "bi-geo-alt",
            text: "Check the pin code on the customer record — the portal validates it against the state.",
        });
    }
    if (msg.includes("distance")) {
        steps.push({ icon: "bi-signpost", text: "Enter the correct transport distance below and regenerate." });
    }
    if (msg.includes("vehicle")) {
        steps.push({ icon: "bi-truck", text: "Correct the vehicle number below (e.g. KA01AB1234, no spaces) and regenerate." });
    }
    if (msg.includes("total")) {
        steps.push({
            icon: "bi-calculator",
            text: "The portal recomputes every total. Ask your admin to check the invoice's item amounts and tax on the server.",
        });
    }
    if (msg.includes("auth") || msg.includes("token") || msg.includes("credential") || msg.includes("password")) {
        steps.push({
            icon: "bi-shield-lock",
            text: "The GST portal login needs attention — ask your admin to verify the e-Invoice credentials on the server's GST Services page.",
        });
    }
    if (msg.includes("duplicate")) {
        steps.push({
            icon: "bi-files",
            text: "The portal already holds an IRN for this invoice number. Ask your admin to fetch it from the server's e-Invoice screen — it cannot be registered twice.",
        });
    }
    if (msg.includes("too soon") || msg.includes("busy") || msg.includes("reach")) {
        steps.push({ icon: "bi-hourglass-split", text: "The portal was busy — simply try Regenerate again in a minute." });
    }
    if (steps.length === 0) {
        steps.push({ icon: "bi-list-check", text: "Review the details below, correct anything that looks off, and regenerate." });
    }
    steps.push({ icon: "bi-arrow-repeat", text: "Then press Regenerate — the request goes through the RevCon server with your fixes applied." });
    return steps;
}

/**
 * Detail / recovery popup for a sale's e-Invoice, opened from the Sales List.
 * GENERATED: hero IRN + acknowledgement, View/Print and Cancel.
 * FAILED:    full portal error, guided next steps, regenerate with corrected data.
 * CANCELLED: struck-out IRN and an explanation that it cannot be re-registered.
 */
export function EInvoiceDetailDialog({ show, sale, onHide, onUpdated }) {
    const navigate = useNavigate();
    const [status, setStatus] = useState("");
    const [irn, setIrn] = useState(null);
    const [invoiceNumber, setInvoiceNumber] = useState(null);
    const [error, setError] = useState(null);
    const [detail, setDetail] = useState(null); // full EInvoice doc from the server
    const [distance, setDistance] = useState("");
    const [vehicleNo, setVehicleNo] = useState("");
    const [busy, setBusy] = useState(false);
    const [copied, setCopied] = useState(false);
    const [cancelMode, setCancelMode] = useState(false);
    const [cancelReason, setCancelReason] = useState(2);
    const [cancelRemark, setCancelRemark] = useState("Data entry mistake");

    useEffect(() => {
        if (!show || !sale) return;
        setStatus(String(sale.einvoiceStatus || "").toUpperCase());
        setIrn(sale.irn || null);
        setInvoiceNumber(sale.invoiceNumber || null);
        setError(sale.einvoiceError || null);
        setDistance(sale.leadKm ? String(Math.round(sale.leadKm)) : "");
        setVehicleNo(sale.vehicleNumber || "");
        setCancelMode(false);
        setDetail(null);

        // Pull the live document so a registration/cancellation done on the server shows here.
        getEInvoiceDetail(sale.id)
            .then(({ data }) => {
                if (!data?.exists) return;
                setDetail(data.eInvoice);
                const live = String(data.eInvoice?.status || "").toUpperCase();
                if (live === "ACTIVE") setStatus("GENERATED");
                if (live === "CANCELLED") setStatus("CANCELLED");
                if (data.eInvoice?.irn) setIrn(data.eInvoice.irn);
                if (data.invoiceNumber) setInvoiceNumber(data.invoiceNumber);
            })
            .catch(() => { /* the local record is good enough to render */ });
    }, [show, sale]);

    useEffect(() => {
        if (!copied) return;
        const timer = setTimeout(() => setCopied(false), 1500);
        return () => clearTimeout(timer);
    }, [copied]);

    const handleRetry = () => {
        setBusy(true);
        retryEInvoice({ saleId: sale.id, transDistance: distance, vehicleNo })
            .then(({ data }) => {
                if (data?.success) {
                    showToast("e-Invoice registered successfully.", TOAST_SUCCESS);
                    setStatus("GENERATED");
                    setIrn(data.irn);
                    setError(null);
                    onUpdated && onUpdated();
                } else {
                    setError(data?.error || "e-Invoice registration failed.");
                    setStatus("FAILED");
                    showToast(data?.error || "e-Invoice registration failed.", TOAST_FAILURE);
                }
            })
            .catch((e) => showToast(e?.response?.data?.error || "Could not reach the server.", TOAST_FAILURE))
            .finally(() => setBusy(false));
    };

    const handleCancel = () => {
        if (!cancelRemark.trim()) {
            showToast("A cancellation remark is required.", TOAST_FAILURE);
            return;
        }
        setBusy(true);
        cancelEInvoiceClient({ saleId: sale.id, cancelRsnCode: cancelReason, cancelRmrk: cancelRemark.trim() })
            .then(({ data }) => {
                if (data?.success) {
                    showToast("e-Invoice cancelled on the GST portal.", TOAST_SUCCESS);
                    setStatus("CANCELLED");
                    setCancelMode(false);
                    onUpdated && onUpdated();
                } else {
                    showToast(data?.error || "e-Invoice cancellation failed.", TOAST_FAILURE);
                }
            })
            .catch((e) => showToast(e?.response?.data?.error || "Could not reach the server.", TOAST_FAILURE))
            .finally(() => setBusy(false));
    };

    if (!sale) return null;

    const generated = status === "GENERATED";
    const cancelled = status === "CANCELLED";
    const failed = status === "FAILED";

    // Same visual language as the e-Way bill dialog: coloured header mark, the reference
    // number as the hero, then a flat grid of supporting facts.
    const headTone = generated
        ? { bg: "bg-light-success", fg: "text-success", icon: "bi-check-circle-fill", title: "E-Invoice Registered" }
        : cancelled
            ? { bg: "bg-light", fg: "text-muted", icon: "bi-slash-circle", title: "E-Invoice Cancelled" }
            : failed
                ? { bg: "bg-light-danger", fg: "text-danger", icon: "bi-exclamation-octagon-fill", title: "E-Invoice Failed" }
                : { bg: "bg-light", fg: "text-muted", icon: "bi-hourglass-split", title: "E-Invoice Pending" };

    const field = (label, value, valueClass = "") => (
        <Col xs={6} md={4}>
            <div className="text-muted" style={{ fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.4px" }}>{label}</div>
            <div className={`fw-semibold small ${valueClass}`}>{value || "\u2014"}</div>
        </Col>
    );

    return (
        <Modal show={show} onHide={() => !busy && onHide()} centered size="lg" scrollable>
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="d-flex align-items-center">
                    <span className={`d-inline-flex align-items-center justify-content-center me-3 rounded ${headTone.bg}`}
                        style={{ width: "38px", height: "38px" }}>
                        <i className={`bi ${headTone.icon} ${headTone.fg} fs-4`}></i>
                    </span>
                    <span>
                        <span className="d-block fs-5 fw-bold text-gray-900">{headTone.title}</span>
                        <span className="d-block fs-8 fw-normal text-muted">
                            Ticket #{sale.ticketSequenceNumber || "-"} &middot; {sale.salesCustomer?.name || "-"}
                        </span>
                    </span>
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="pt-3">
                {/* Hero: the IRN itself. It is 64 characters, so it wraps rather than being
                    blown up like an e-Way bill number — legibility beats drama here. */}
                {(generated || cancelled) && irn && (
                    <div className="rounded p-4 mb-3 text-center"
                        style={generated
                            ? { background: "#e8fff3", border: "1px dashed #50cd89" }
                            : { background: "#f5f6f8", border: "1px dashed #c4c9d0" }}>
                        <div className="text-muted mb-2" style={{ fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "1px" }}>
                            Invoice Reference Number (IRN)
                        </div>
                        <div className={`fw-bold text-break ${generated ? "" : "text-muted text-decoration-line-through"}`}
                            style={{
                                fontFamily: "monospace",
                                fontSize: "0.8rem",
                                lineHeight: 1.6,
                                letterSpacing: "0.5px",
                                color: generated ? "#0f9d58" : undefined,
                                cursor: "pointer",
                            }}
                            title={copied ? "Copied!" : "Click to copy"}
                            onClick={() => { navigator.clipboard?.writeText(irn || ""); setCopied(true); }}>
                            {irn}
                            <i className={`bi ${copied ? "bi-clipboard-check-fill text-success" : "bi-clipboard text-muted"} ms-2`}
                                style={{ verticalAlign: "middle" }}></i>
                        </div>
                        <span className={`badge mt-2 ${cancelled ? "bg-light text-muted" : "bg-light-success text-success"}`}>
                            {cancelled ? "Cancelled" : "Active"}
                        </span>
                    </div>
                )}

                {/* Details grid */}
                {(generated || cancelled) && (
                    <div className="bg-light rounded p-3 mb-3">
                        <Row className="g-3">
                            {field("Server Invoice", invoiceNumber)}
                            {field("Acknowledgement No", detail?.ackNo)}
                            {field("Registered On", detail?.ackDt)}
                            {detail?.ewbNo && field("E-Way Bill", detail.ewbNo)}
                            {detail?.ewbNo && field("E-Way Valid Until", detail.ewbValidTill)}
                            {cancelled && field("Cancelled On", detail?.cancelDate)}
                        </Row>
                    </div>
                )}

                {generated && !cancelMode && (
                    <div className="d-flex align-items-start text-muted" style={{ fontSize: "0.75rem" }}>
                        <i className="bi bi-info-circle me-2 mt-1"></i>
                        <span>
                            This invoice is registered with the government — the IRN and its QR code are the
                            proof. Use <strong>View / Print</strong> for the official copy
                            {detail?.ewbNo ? " (the e-Way bill is on the same sheet)" : ""}, or cancel it within
                            <strong> 24 hours</strong> of registration if something is wrong.
                        </span>
                    </div>
                )}

                {/* The hero already shows the struck-through IRN and a Cancelled badge, so this
                    says only the part that is not obvious: it can never be reissued. */}
                {cancelled && (
                    <Alert variant="secondary" className="fs-8 py-2 px-3 mb-0">
                        The GST portal does not allow a new IRN on the same invoice number. Raise a new sale
                        if this load still needs to be invoiced.
                    </Alert>
                )}

                {failed && (
                    <>
                        <Alert variant="danger" className="fs-7">
                            <div className="fw-bold mb-1">
                                <i className="bi bi-exclamation-octagon me-2" />The GST portal rejected this e-Invoice
                            </div>
                            <div>{error || "Unknown error."}</div>
                        </Alert>

                        <div className="border border-gray-300 border-dashed rounded p-4 mb-4">
                            <div className="fw-bold fs-7 mb-3">What to do next</div>
                            {nextSteps(error).map((step, i) => (
                                <div key={i} className="d-flex align-items-start mb-2">
                                    <i className={`bi ${step.icon} text-primary me-3 mt-1`} />
                                    <div className="fs-8 text-gray-700">{step.text}</div>
                                </div>
                            ))}
                        </div>

                        <Row className="g-3">
                            <Col md={6}>
                                <Form.Label className="fs-8 text-muted">Transport distance (km)</Form.Label>
                                <Form.Control
                                    size="sm"
                                    type="number"
                                    value={distance}
                                    onChange={(e) => setDistance(e.target.value)}
                                    placeholder="Only needed if an e-Way bill is generated too"
                                />
                            </Col>
                            <Col md={6}>
                                <Form.Label className="fs-8 text-muted">Vehicle number</Form.Label>
                                <Form.Control
                                    size="sm"
                                    value={vehicleNo}
                                    onChange={(e) =>
                                        setVehicleNo(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10))
                                    }
                                    placeholder="KA01AB1234"
                                />
                            </Col>
                        </Row>
                    </>
                )}

                {generated && cancelMode && (
                    <div className="border border-danger border-dashed rounded p-4 mt-4">
                        <div className="fw-bold fs-7 mb-3">Cancel this e-Invoice</div>
                        <Row className="g-3">
                            <Col md={5}>
                                <Form.Label className="fs-8 text-muted">Reason</Form.Label>
                                <Form.Select
                                    size="sm"
                                    value={cancelReason}
                                    onChange={(e) => setCancelReason(Number(e.target.value))}
                                >
                                    {CANCEL_REASONS.map((r) => (
                                        <option key={r.value} value={r.value}>{r.label}</option>
                                    ))}
                                </Form.Select>
                            </Col>
                            <Col md={7}>
                                <Form.Label className="fs-8 text-muted">Remark</Form.Label>
                                <Form.Control
                                    size="sm"
                                    maxLength={100}
                                    value={cancelRemark}
                                    onChange={(e) => setCancelRemark(e.target.value)}
                                />
                            </Col>
                        </Row>
                        <div className="text-muted fs-8 mt-3">
                            Only possible within 24 hours of registration, and the invoice number can never be
                            registered again.
                        </div>
                    </div>
                )}
            </Modal.Body>

            {/* Button variants, order and icons mirror the e-Way bill dialog: the primary
                action sits rightmost, destructive actions use the soft danger tone rather
                than an outline, and cancel mode swaps Close for Back so there is always a
                way out that is not "confirm". */}
            <Modal.Footer className="border-0 pt-0">
                {generated && cancelMode ? (
                    <>
                        <Button variant="light" size="sm" onClick={() => setCancelMode(false)} disabled={busy}>
                            Back
                        </Button>
                        <Button variant="danger" size="sm" onClick={handleCancel}
                            disabled={busy || !cancelRemark.trim()}>
                            {busy
                                ? <><Spinner size="sm" animation="border" className="me-2" />Cancelling…</>
                                : <><i className="bi bi-slash-circle me-2"></i>Confirm Cancel</>}
                        </Button>
                    </>
                ) : (
                    <>
                        <Button variant="light" size="sm" onClick={onHide} disabled={busy}>Close</Button>
                        {generated && (
                            <>
                                <Button variant="light-danger" size="sm" onClick={() => setCancelMode(true)} disabled={busy}>
                                    <i className="bi bi-slash-circle me-2"></i>Cancel E-Invoice
                                </Button>
                                <Button variant="success" size="sm"
                                    onClick={() => { onHide(); navigate(`/einvoice-print/${sale.id}`); }}>
                                    <i className="bi bi-printer me-2"></i>View / Print
                                </Button>
                            </>
                        )}
                        {failed && (
                            <Button variant="primary" size="sm" onClick={handleRetry} disabled={busy}>
                                {busy
                                    ? <><Spinner size="sm" animation="border" className="me-2" />Registering…</>
                                    : <><i className="bi bi-arrow-repeat me-2"></i>Regenerate E-Invoice</>}
                            </Button>
                        )}
                        {cancelled && (
                            <Button variant="success" size="sm"
                                onClick={() => { onHide(); navigate(`/einvoice-print/${sale.id}`); }}>
                                <i className="bi bi-printer me-2"></i>View / Print
                            </Button>
                        )}
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
}
